import { useRef, type MouseEvent } from "react";
import SparksSvg from "../../../../assets/reactions/sparks.svg?react";
import BlossomSvg from "../../../../assets/reactions/blossom.svg?react";
import CoolSvg from "../../../../assets/reactions/cool.svg?react";
import HeartSvg from "../../../../assets/reactions/heart.svg?react";
import OctopusSvg from "../../../../assets/reactions/octopus.svg?react";
import cx from "../../../../lib/cx.js";
import { icons } from "../../icons";
import buttonStyles from "../Button/Button.css";
import styles from "./ReactButton.css";

// Keyed by the emoji the API stores for each reaction.
export const reactions = {
  "✨": { Icon: icons.sparkle, label: "Sparks" },
} as const;

// The illustrated set the full showroom entry still uses.
export const legacyReactions = {
  "✨": { Icon: SparksSvg, label: "Sparks" },
  "🌱": { Icon: BlossomSvg, label: "Pflanze" },
  "🆒": { Icon: CoolSvg, label: "Cool" },
  "❤️": { Icon: HeartSvg, label: "Herz" },
  "🐙": { Icon: OctopusSvg, label: "Oktopus" },
} as const;

export type Reaction = keyof typeof reactions;
export type LegacyReaction = keyof typeof legacyReactions;

type ReactButtonProps = {
  count: number;
  selected?: boolean;
  // May return a promise: further clicks are ignored until it settles.
  onClick?: (e: MouseEvent<HTMLButtonElement>) => unknown;
} & (
  | { legacy?: false; reaction: Reaction }
  | { legacy: true; reaction: LegacyReaction }
);

/**
 * A reaction with how often it was given, toggled by the viewer. It wears the
 * `Button`'s small size: neutral secondary until selected, accent primary once
 * it is.
 */
const ReactButton = ({
  count,
  selected = false,
  onClick,
  ...props
}: ReactButtonProps) => {
  const pendingRef = useRef(false);
  const { Icon, label } = props.legacy
    ? legacyReactions[props.reaction]
    : reactions[props.reaction];

  const handleClick = async (e: MouseEvent<HTMLButtonElement>) => {
    if (!onClick || pendingRef.current) return;
    pendingRef.current = true;
    try {
      await onClick(e);
    } catch (error) {
      console.error(error);
    } finally {
      pendingRef.current = false;
    }
  };

  return (
    <button
      type="button"
      aria-label={`${label} (${count})`}
      aria-pressed={selected}
      className={cx(
        buttonStyles.button({
          size: "small",
          color: selected ? "accent" : "neutral",
          variant: selected ? "primary" : "secondary",
        }),
        styles.reactButton,
      )}
      onClick={(e) => void handleClick(e)}
    >
      <Icon
        className={props.legacy ? styles.legacyIcon : styles.icon}
        aria-hidden
      />
      <span className={styles.count}>{count}</span>
    </button>
  );
};

export default ReactButton;
